// ============================================================
// ELC Shift — Uploads / Staff (Amanah HR apply) module
//
// Apply / cancel / status helpers for the staff bulk upload
// flow. The earlier stages (file parse → store mapping → diff
// preview) are still inline in admin.html as of v95.0; this
// sprint only extracts the "apply changes to DB" tail end plus
// the shared status display.
//
// Public surface (callable from admin.html inline handlers):
//   Uploads.staff.apply()              — apply parsed diff to DB.staff
//                                        + optional DB.balances write
//   Uploads.staff.cancel()             — clear preview state + UI
//   Uploads.staff.showStatus(type,msg) — render the status banner
//
// State shared with inline admin.html code (declared as `var`
// in admin.html so it lives on `window` — both inline code AND
// this module read/write via bare identifier, with `window.X`
// as the backing storage):
//   - staffDryRun         (read by apply: blocks DB writes when true)
//   - staffDiffResult     (read by apply; reset by apply + cancel)
//   - staffUploadParsed   (reset by apply + cancel)
//   - staffStoreMapping   (reset by apply + cancel)
//   - staffData           (read by apply: color rotation pool)
//
// Free globals (admin.html lexical scope):
//   - showToast            (toast helper)
//   - getStoreArea         (area lookup by store name)
//   - getStoreCountry      (country lookup by store name)
//   - loadStaffFromDB      (refresh staff cache after writes)
//   - renderStaff          (re-render the staff directory)
//   - renderDashboard      (re-render dashboard counters)
//   - populateStoreSelects (refresh store dropdowns)
//   - DB                   (db.js facade: DB.staff.*, DB.balances.*)
//
// DOM elements (in admin.html markup, referenced by id):
//   - amanahApplyBalances (opt-in checkbox for balance writes)
//   - applyStaffUploadBtn (the trigger button)
//   - applyProgress{Wrap,Bar,Label,Count,Eta} (progress UI)
//   - staffDiffCard       (results container)
//   - storeMappingCard    (mapping UI)
//   - staffFileInput      (file input, reset on done)
//   - staffUploadStatus   (status banner container)
//   - staffUploadStatusInner (status banner inner)
// ============================================================

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { setHTML } from '../shared/set-html.js';   // v136.8: null-safe innerHTML for admin renders

const COLORS_POOL = [
  'var(--brand,#009E3D)','#128c7e','#0ea5e9','#8b5cf6','#f59e0b','#ef4444',
  '#ec4899','#06b6d4','#10b981','#6366f1','#f97316','#84cc16',
  '#14b8a6','#a855f7','#3b82f6',
];

export async function apply() {
  if (staffDryRun) { showToast('Dry-run mode is on — nothing was applied', 'info'); return; }

  // v58: explicit archive confirmation. If any staff would be archived (i.e. active at a
  // scoped store but absent from the file), prompt BEFORE we do anything else. This
  // protects against the common mistake of the Amanah data contact sending a partial file and the
  // app happily archiving half the roster.
  const toArchive = staffDiffResult.filter(r => r.status === 'missing');
  if (toArchive.length) {
    const preview = toArchive.slice(0, 5).map(r => `  • ${r.existing.name}  (${r.existing.store})`).join('\n');
    const more = toArchive.length > 5 ? `\n  …and ${toArchive.length - 5} more` : '';
    const msg =
      `This upload will ARCHIVE ${toArchive.length} staff member${toArchive.length === 1 ? '' : 's'}:\n\n` +
      `${preview}${more}\n\n` +
      `They are currently active at a store covered by this file, but do not appear in the file.\n\n` +
      `If this looks wrong — e.g. the file only covers one country and you didn't expect the other to be touched — CANCEL NOW, check the scope banner, and re-upload the correct file.\n\n` +
      `Archiving cannot be undone from this screen. Continue?`;
    if (!confirm(msg)) {
      showToast('Upload cancelled. Nothing was changed.', 'info');
      return;
    }
  }

  // v52: Leave balance write is OFF by default — opt-in via toggle.
  const applyBalances = !!document.getElementById('amanahApplyBalances')?.checked;
  const balanceAuditLog = []; // collected for console after apply

  let added = 0, changed = 0, archived = 0, failed = 0, balanceWrites = 0;
  const errors = [];
  const btn = document.getElementById('applyStaffUploadBtn');
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Applying…'; }

  // v58: progress bar
  // Count rows that will actually be written (skips unchanged + errors). Anything
  // being archived (missing) also counts since it's a DB call.
  const totalSteps = staffDiffResult.filter(r =>
    r.status === 'add' || r.status === 'change' || r.status === 'missing'
  ).length;
  const progressWrap  = document.getElementById('applyProgressWrap');
  const progressBar   = document.getElementById('applyProgressBar');
  const progressLabel = document.getElementById('applyProgressLabel');
  const progressCount = document.getElementById('applyProgressCount');
  const progressEta   = document.getElementById('applyProgressEta');
  if (progressWrap) progressWrap.style.display = 'block';
  if (progressCount) progressCount.textContent = '0 / ' + totalSteps;
  if (progressBar)   progressBar.style.width = '0%';
  const tStart = Date.now();
  let stepsDone = 0;
  // v135.2: the name goes in its own translate="no" span, so 'Added' is a label the Arabic can swap
  const bumpProgress = (label, who) => {
    stepsDone++;
    if (progressCount) progressCount.textContent = stepsDone + ' / ' + totalSteps;
    if (progressBar)   progressBar.style.width = (totalSteps ? (stepsDone / totalSteps * 100) : 100) + '%';
    if (progressLabel) {
      progressLabel.textContent = (label || 'Applying…') + (who != null ? ' ' : '');
      if (who != null) {
        const sp = document.createElement('span');
        sp.setAttribute('translate', 'no');
        sp.textContent = who;
        progressLabel.appendChild(sp);
      }
    }
    if (progressEta && stepsDone > 2 && stepsDone < totalSteps) {
      const elapsed = (Date.now() - tStart) / 1000;
      const remaining = (elapsed / stepsDone) * (totalSteps - stepsDone);
      progressEta.textContent = '~' + Math.ceil(remaining) + 's remaining';
    } else if (progressEta && stepsDone >= totalSteps) {
      progressEta.textContent = 'Complete in ' + ((Date.now() - tStart) / 1000).toFixed(1) + 's';
    }
  };

  for (const r of staffDiffResult) {
    if (r.status === 'error' || r.status === 'unchanged') {
      // skipped — no DB call, no progress tick
      continue;
    }
    const displayName = r.incoming ? r.incoming.fullName : (r.existing ? r.existing.name : '—');
    try {
      if (r.status === 'add') {
        const inc = r.incoming;
        const color = COLORS_POOL[(staffData.length + added) % COLORS_POOL.length];
        const area    = getStoreArea(inc.appStore)    || '';
        const country = getStoreCountry(inc.appStore) || 'ELC - UAE';
        // v58: schema confirmed — all these columns exist on the staff table.
        await DB.staff.create({
          name: inc.fullName, role: inc.role,
          store: inc.appStore,
          area_id: area, country,
          color, pin: '000000', active: true,
          contracted_hours: 48,
          emp_code: inc.empCode || null,
          on_notice: !!inc.onNotice,
        });
        added++;
        bumpProgress('Added', displayName);
      } else if (r.status === 'change') {
        const patch = {};
        r.changed.forEach(c => {
          if (c.field === 'name')       patch.name = c.to;
          if (c.field === 'role')       patch.role = c.to;
          if (c.field === 'store')     { patch.store = c.to; }
          if (c.field === 'emp_code')   patch.emp_code = c.to || null;
          if (c.field === 'on_notice')  patch.on_notice = c.to === 'yes';
        });
        if (patch.store) {
          const area = getStoreArea(patch.store) || '';
          patch.area_id = area;
          patch.country  = getStoreCountry(patch.store) || 'ELC - UAE';
        }
        await DB.staff.update(r.existing.id, patch);
        changed++;
        bumpProgress('Updated', displayName);
      } else if (r.status === 'missing') {
        await DB.staff.archive(r.existing.id);
        archived++;
        bumpProgress('Archived', displayName);
      }
    } catch (err) {
      failed++;
      errors.push(`${displayName}: ${err.message}`);
      console.error('[Amanah apply]', err);
      bumpProgress('❌ Failed:', displayName);
    }
  }

  // ── v52: BALANCE WRITE PASS ─────────────────────────────────────────
  // Runs only when the "Update leave balances" toggle is ON.
  // Needs to run AFTER the staff creates above so new staff have IDs.
  // We reload staff here so new additions have DB-assigned IDs.
  if (applyBalances && !failed) {
    try { await loadStaffFromDB(); } catch(e) { console.warn('[Amanah apply] reload pre-balance failed:', e.message); }

    const year = new Date().getFullYear();
    // Existing balance rows keyed by staff_id so we can read current holiday_total
    let existingBalances = {};
    try {
      const rows = await DB.balances.listAdmin(year);
      (rows || []).forEach(row => { existingBalances[row.staff_id] = row; });
    } catch(e) { console.warn('[Amanah apply] balance list failed — using defaults:', e.message); }

    const rowsToWrite = [];
    for (const r of staffDiffResult) {
      if (r.status === 'error' || r.status === 'missing' || r.status === 'unchanged') continue;
      const inc = r.incoming;
      if (!inc) continue;
      // v58: support both old ("sickLeaveDays" = total entitlement) and new ("sickLeaveRemaining" = days remaining)
      const hasSickRemaining = typeof inc.sickLeaveRemaining === 'number';
      const hasSickTotal     = typeof inc.sickLeaveDays === 'number';   // legacy format
      const hasAnn           = typeof inc.annualLeaveRemaining === 'number';
      if (!hasSickRemaining && !hasSickTotal && !hasAnn) continue;

      // Resolve staff_id: for changes we have existing.id; for adds we look up by emp_code in fresh staffData
      let staffId = r.existing ? r.existing.id : null;
      if (!staffId && inc.empCode) {
        const justAdded = staffData.find(s => s.empCode === inc.empCode);
        if (justAdded) staffId = justAdded.id;
      }
      if (!staffId) {
        console.warn('[Amanah balances] could not resolve staff_id for', inc.fullName);
        continue;
      }

      const existing = existingBalances[staffId] || {};
      const holTotal = (typeof existing.holiday_total === 'number' && existing.holiday_total > 0) ? existing.holiday_total : 30;
      const sickTotalCurrent = (typeof existing.sick_total === 'number') ? existing.sick_total : 15;
      const sickUsedCurrent  = (typeof existing.sick_used  === 'number') ? existing.sick_used  : 0;

      // Start with current values; only overwrite fields the file provides (per Matt: blank = no override)
      let newSickTotal = sickTotalCurrent;
      let newSickUsed  = sickUsedCurrent;
      let   newHolUsed = (typeof existing.holiday_used === 'number') ? existing.holiday_used : 0;
      const balanceNotes = [];

      // v58 Sick leave handling — NEW FORMAT (remaining days): compute used from total - remaining
      // Legacy behaviour for the old format (total entitlement): overwrite sick_total, preserve sick_used
      if (hasSickRemaining) {
        // Compute used: want (newSickTotal - newSickUsed) === inc.sickLeaveRemaining
        // Overused possible: if remaining < 0, user has taken more than total → reflect in used
        const sickRem = inc.sickLeaveRemaining;
        if (sickRem > sickTotalCurrent) {
          // Rare: file says more remaining than the app's total — flag for review, don't touch total
          newSickUsed = 0;
          balanceNotes.push('sick remaining ' + sickRem + 'd > app total ' + sickTotalCurrent + 'd — clamped used=0; review sick_total manually');
        } else {
          newSickUsed = sickTotalCurrent - sickRem;
          if (sickRem < 0) balanceNotes.push('sick overused: file says ' + sickRem + 'd remaining, used=' + newSickUsed);
        }
        // sick_total unchanged
      } else if (hasSickTotal) {
        // Legacy: file reports total entitlement → overwrite sick_total, preserve sick_used
        newSickTotal = inc.sickLeaveDays;
      }

      // Annual-leave handling (unchanged between formats): compute used from total - remaining
      if (hasAnn) {
        if (inc.annualLeaveRemaining < 0) {
          // Overused — legitimate. Preserve the overage by letting used > total.
          newHolUsed = holTotal - inc.annualLeaveRemaining; // e.g. 30 - (-2) = 32
          balanceNotes.push('hol overused: file says ' + inc.annualLeaveRemaining + 'd remaining, used=' + newHolUsed);
        } else if (inc.annualLeaveRemaining > holTotal) {
          // Rare: Amanah says more remaining than app's total — clamp used to 0, flag for review
          newHolUsed = 0;
          balanceNotes.push('remaining ' + inc.annualLeaveRemaining + 'd > app total ' + holTotal + 'd — clamped used=0; review holiday_total manually');
        } else {
          newHolUsed = holTotal - inc.annualLeaveRemaining;
        }
      }

      balanceAuditLog.push({
        staff_id: staffId, emp_code: inc.empCode, name: inc.fullName,
        before: { holiday_total: holTotal, holiday_used: existing.holiday_used ?? 0, sick_total: sickTotalCurrent, sick_used: sickUsedCurrent },
        from_file: {
          sickLeaveRemaining: hasSickRemaining ? inc.sickLeaveRemaining : null,
          sickLeaveDays: hasSickTotal ? inc.sickLeaveDays : null,
          annualLeaveRemaining: hasAnn ? inc.annualLeaveRemaining : null,
        },
        after: { holiday_total: holTotal, holiday_used: newHolUsed, sick_total: newSickTotal, sick_used: newSickUsed },
        notes: balanceNotes.length ? balanceNotes.join(' | ') : null,
      });

      rowsToWrite.push({
        staff_id: staffId,
        year,
        holiday_total: holTotal,          // preserve — we don't overwrite this from Amanah
        holiday_used:  newHolUsed,        // computed: total - remaining
        sick_total:    newSickTotal,      // overwrite in legacy format, preserve in new format
        sick_used:     newSickUsed,       // computed from remaining (new format) or preserved (legacy)
      });

      // Keep localStorage mirrors in sync
      localStorage.setItem('elc_hol_total_' + staffId, String(holTotal));
      localStorage.setItem('elc_hol_bal_'   + staffId, String(Math.max(0, holTotal - newHolUsed)));
      localStorage.setItem('elc_sick_total_'+ staffId, String(newSickTotal));
      localStorage.setItem('elc_sick_bal_'  + staffId, String(Math.max(0, newSickTotal - newSickUsed)));
    }

    if (rowsToWrite.length) {
      if (progressLabel) progressLabel.textContent = 'Writing ' + rowsToWrite.length + ' balance rows…';
      try {
        await DB.balances.saveAdmin(rowsToWrite);
        balanceWrites = rowsToWrite.length;
        console.log('[Amanah balances] Wrote', balanceWrites, 'balance rows. Audit log:');
        console.table(balanceAuditLog);
      } catch(e) {
        console.error('[Amanah balances] save failed:', e);
        // v136.8 (16 Oct audit): the balances go up in ONE batch, so a failure means none of these rows were
        // saved. It used to add one line to errors without counting it, so the import still showed a green
        // "Done". Every person is now counted as a failure and named.
        failed += rowsToWrite.length;
        balanceAuditLog.forEach((a) => errors.push('Balance not saved for ' + (a.name || ('staff #' + a.staff_id)) +
          (a.emp_code ? ' (' + a.emp_code + ')' : '') + ': ' + ((e && e.message) || 'unknown error')));
      }
    }
  }

  // v58: finalise progress bar
  if (progressBar)   progressBar.style.width = '100%';
  if (progressLabel) progressLabel.textContent = failed ? ('Complete with ' + failed + ' error' + (failed === 1 ? '' : 's')) : 'Complete';
  // Leave the bar visible for 2s so the user sees the 100% state, then hide
  setTimeout(() => { if (progressWrap) progressWrap.style.display = 'none'; }, 2500);

  await loadStaffFromDB();
  if (typeof renderStaff === 'function') renderStaff();
  if (typeof renderDashboard === 'function') renderDashboard();
  if (typeof populateStoreSelects === 'function') populateStoreSelects();

  document.getElementById('staffDiffCard').style.display = 'none';
  document.getElementById('storeMappingCard').style.display = 'none';
  staffDiffResult = []; staffUploadParsed = []; staffStoreMapping = {};
  const fi = document.getElementById('staffFileInput'); if (fi) fi.value = '';

  let summary = `+${added} added · ${changed} updated · ${archived} archived`;
  if (applyBalances) summary += ` · ${balanceWrites} balance row(s)`;
  if (failed) summary += ` · ${failed} failed`;
  showStatus(failed ? 'error' : 'success',
    // v136.8: "Done with N failures" and the whole list (it showed the first five under "Partial").
    (failed ? '⚠️ Done with ' + failed + ' failure' + (failed === 1 ? '' : 's') + ': ' : '✅ Done: ') + summary +
    (applyBalances ? '\n(Balance audit log written to browser console)' : '') +
    (failed ? '\n\nNot saved:\n' + errors.slice(0, 50).join('\n') +
      (errors.length > 50 ? '\n...and ' + (errors.length - 50) + ' more (listed in the browser console)' : '') : ''));
  if (failed) console.warn('[Amanah import] not saved:', errors);
  showToast('Amanah import complete: ' + summary, failed ? 'error' : 'success');
}

export function cancel() {
  staffDiffResult = []; staffUploadParsed = []; staffStoreMapping = {};
  document.getElementById('staffDiffCard').style.display = 'none';
  const m = document.getElementById('storeMappingCard'); if (m) m.style.display = 'none';
  document.getElementById('staffUploadStatus').style.display = 'none';
  const fi = document.getElementById('staffFileInput'); if (fi) fi.value = '';
}

export function showStatus(type, msg) {
  const el = document.getElementById('staffUploadStatus');
  const inner = document.getElementById('staffUploadStatusInner');
  if (!el || !inner) return;
  el.style.display = '';
  const bg    = type === 'success' ? 'var(--adm-ok-pale)' : type === 'error' ? 'var(--adm-danger-pale)' : 'var(--warn-bg)';
  const color = type === 'success' ? 'var(--ok-deep)' : type === 'error' ? 'var(--danger-deep)' : 'var(--warn-deep)';
  inner.style.cssText = `background:${bg};color:${color};border-radius:10px;padding:12px 14px;font-size:13px;white-space:pre-wrap`;
  inner.textContent = msg;
}


// ════════════════════════════════════════════════════════════
// v98.0 additions — Diff + store-mapping UI
// ════════════════════════════════════════════════════════════
// The full staff bulk upload diff + mapping interactive UI moved
// into this module. The "earlier stages (file parse → store mapping
// → diff preview) are still inline in admin.html" comment in the
// header docblock above is now partially obsolete — the mapping +
// diff stages are HERE. Only the file parsing pipeline remains
// inline (Sprint 11 candidate).
//
// Eleven functions, ~355 LOC moved:
//   • Pure data: findExistingByAmanah, findSuggestedMatches
//   • State + UI: toggleAmanahDryRun, updateApplyButtonState
//   • Match interactions: amanahAcceptMatch, amanahDismissSuggestion,
//                          amanahUndoMatch
//   • Store mapping: updateStoreMapping, renderStoreMappingCard
//   • Diff: buildAmanahDiff, renderDiffTable
//
// New state shared via window (admin.html `var` declarations):
//   - diffCurrentFilter (converted let→var in v98 for cross-module access)
//
// Match utilities (Uploads.match.X) are referenced directly — the global
// Uploads object is bound by the bundle entry before any module runs,
// so `Uploads.match.X` resolves correctly at call time.

export function findExistingByAmanah(incoming) {
  // 1. By emp_code (exact)
  if (incoming.empCode) {
    const byCode = staffData.find(s => s.empCode && s.empCode === incoming.empCode);
    if (byCode) return byCode;
  }
  // 2. Exact whitespace-normalised name
  const incNorm = Uploads.match.normAmanahName(incoming.fullName);
  const byExactName = staffData.find(s => s.active !== false && Uploads.match.normAmanahName(s.name) === incNorm);
  if (byExactName) return byExactName;

  // 3. v54: strong fuzzy name match — first + last tokens both match (with ≤1 char typo
  // tolerance on the first name). Catches "Jane Mary Doe" ↔ "Jane Doe",
  // "Jon Smith" ↔ "John Paul Smith". Only auto-matches within the
  // same app store to avoid cross-store false positives (different people can share names).
  const storeForMatch = incoming.appStore;
  if (storeForMatch) {
    const strong = staffData.find(s =>
      s.active !== false &&
      s.store === storeForMatch &&
      !(s.empCode && incoming.empCode && s.empCode !== incoming.empCode) && // don't override a conflicting emp_code
      Uploads.match.classifyNameMatch(s.name, incoming.fullName) === 'strong'
    );
    if (strong) return strong;
  }
  return null;
}

export function findSuggestedMatches(incoming, matchedExistingIds) {
  if (!incoming.appStore) return [];
  const candidates = [];
  staffData.forEach(s => {
    if (s.active === false) return;
    if (s.store !== incoming.appStore) return;
    if (matchedExistingIds.has(s.id)) return;
    if (s.empCode && incoming.empCode && s.empCode !== incoming.empCode) return; // conflicting codes — definitely different
    const cls = Uploads.match.classifyNameMatch(s.name, incoming.fullName);
    if (cls === 'strong' || cls === 'moderate') candidates.push({ existing: s, confidence: cls });
  });
  // Sort strong first
  candidates.sort((a, b) => (a.confidence === 'strong' ? -1 : 1));
  return candidates;
}

export function toggleAmanahDryRun(el) {
  staffDryRun = !!el.checked;
  updateApplyButtonState();
}

export function updateApplyButtonState() {
  const btn = document.getElementById('applyStaffUploadBtn');
  if (!btn) return;
  const hasErrors = staffDiffResult.some(r => r.status === 'error');
  const hasWork   = staffDiffResult.some(r => ['add','change','missing'].includes(r.status));

  if (staffDryRun) {
    btn.disabled = true;
    btn.textContent = '🔒 Dry run — turn off to apply';
    btn.title = 'Dry-run mode is on. Uncheck "Dry run" to enable writing changes.';
  } else if (hasErrors) {
    btn.disabled = true;
    btn.textContent = '⚠ Fix errors first';
    btn.title = 'Fix all rows showing errors before applying.';
  } else if (!hasWork) {
    btn.disabled = true;
    btn.textContent = 'No changes to apply';
    btn.title = '';
  } else {
    btn.disabled = false;
    btn.textContent = '✅ Apply Changes';
    btn.title = '';
  }
}

export function amanahAcceptMatch(empCode, existingId) {
  if (!window._amanahManualMatches) window._amanahManualMatches = {};
  window._amanahManualMatches[empCode] = existingId;
  buildAmanahDiff();
}

export function amanahDismissSuggestion(empCodeOrName) {
  if (!window._amanahDismissedSuggestions) window._amanahDismissedSuggestions = {};
  window._amanahDismissedSuggestions[empCodeOrName] = true;
  buildAmanahDiff();
}

export function amanahUndoMatch(empCode) {
  if (window._amanahManualMatches) delete window._amanahManualMatches[empCode];
  buildAmanahDiff();
}

// v136.8 (security audit M3): names, roles, emp codes and store names in the HR import and the
// staff list are typed text, and they went into innerHTML raw (stored XSS in the admin app).
// escText for text and plain attributes; jsArg inside a quoted inline-handler argument: JS-escape
// first, THEN HTML-escape (the v132.85 house rule: escaping alone lets &#39; decode back to a quote
// before the handler runs).
function escText(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function jsArg(s) {
  return escText(String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r?\n/g, '\\n'));
}

export function updateStoreMapping(amanahName, appName) {
  staffStoreMapping[amanahName] = appName || null;
  staffUploadParsed.forEach(r => { if (r.amanahStore === amanahName) r.appStore = staffStoreMapping[amanahName] || null; });
  renderStoreMappingCard();
  buildAmanahDiff();
}

export function renderStoreMappingCard() {
  const card = document.getElementById('storeMappingCard');
  if (!card) return;
  const amanahStores = Object.keys(staffStoreMapping).sort();
  const unmapped = amanahStores.filter(a => !staffStoreMapping[a]).length;

  const rows = amanahStores.map(a => {
    const appName = staffStoreMapping[a] || '';
    const count = staffUploadParsed.filter(r => r.amanahStore === a).length;
    const status = appName
      ? '<span style="color:var(--green);font-weight:600">✓ matched</span>'
      : '<span style="color:var(--amber);font-weight:600">⚠ not matched</span>';
    const opts = ['<option value="">— select app store —</option>']
      .concat(ALL_STORES.map(s => {
        const sel = s === appName ? ' selected' : '';
        return `<option value="${escText(s)}"${sel} translate="no">${escText(s)}</option>`;
      })).join('');
    const safeA = jsArg(a);   // v136.8 (M3): the store name comes from the HR file
    return `<tr>
      <td translate="no" style="font-size:11px;color:var(--sub)">${escText(a)}</td>
      <td><select class="filter-input" onchange="Uploads.staff.updateStoreMapping('${safeA}', this.value)" style="min-width:240px;font-size:12px;padding:4px 8px">${opts}</select></td>
      <td style="font-size:11px;text-align:center">${count}</td>
      <td style="font-size:11px">${status}</td>
    </tr>`;
  }).join('');

  card.innerHTML = `
    <div class="card-title" style="margin-bottom:10px">
      Store Name Mapping
      <span style="font-weight:400;color:var(--sub);font-size:12px">(${amanahStores.length - unmapped}/${amanahStores.length} auto-matched${unmapped ? ' · ' + unmapped + ' need review' : ''})</span>
    </div>
    <div style="max-height:260px;overflow-y:auto">
      <table class="data-table" style="font-size:12px">
        <thead><tr><th>Amanah name</th><th>App store</th><th>Staff</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  card.style.display = '';
}

export function buildAmanahDiff() {
  const scopedAppStores = new Set(Object.values(staffStoreMapping).filter(Boolean));

  staffDiffResult = [];

  // v54: Track which incoming rows the user has manually matched via the [Match] button,
  // keyed by incoming emp_code → existing staff id. Persists across re-renders.
  if (!window._amanahManualMatches) window._amanahManualMatches = {};
  const manualMatches = window._amanahManualMatches;

  // v54: Also track which pairs the user has explicitly kept separate so the
  // suggestion doesn't keep reappearing after they dismiss it.
  if (!window._amanahDismissedSuggestions) window._amanahDismissedSuggestions = {};

  staffUploadParsed.forEach(incoming => {
    const errors = [];
    if (!incoming.appStore) errors.push('store not mapped — fix in the mapping table above');
    if (!incoming.fullName) errors.push('name missing');
    if (!Uploads.match.AMANAH_VALID_ROLES.includes(incoming.role)) errors.push(`designation "${incoming.designation}" not recognised`);

    // v54: resolve any manual match first, then fall back to normal resolution
    let existing = null;
    const manualId = manualMatches[incoming.empCode];
    if (manualId) existing = staffData.find(s => s.id === manualId) || null;
    if (!existing) existing = findExistingByAmanah(incoming);

    if (errors.length) { staffDiffResult.push({ status: 'error', incoming, existing, errors }); return; }
    if (!existing)     { staffDiffResult.push({ status: 'add',   incoming, existing: null, errors: [] }); return; }

    const changed = [];
    if (existing.name !== incoming.fullName) changed.push({ field:'name',      from: existing.name, to: incoming.fullName });
    if (existing.role !== incoming.role)     changed.push({ field:'role',      from: existing.role, to: incoming.role });
    if (existing.store !== incoming.appStore)changed.push({ field:'store',     from: existing.store, to: incoming.appStore });
    if ((existing.empCode || '') !== incoming.empCode) changed.push({ field:'emp_code', from: existing.empCode || '', to: incoming.empCode });
    if (!!existing.onNotice !== !!incoming.onNotice)   changed.push({ field:'on_notice', from: existing.onNotice ? 'yes' : 'no', to: incoming.onNotice ? 'yes' : 'no' });

    if (changed.length) staffDiffResult.push({ status:'change', incoming, existing, changed, errors: [] });
    else                staffDiffResult.push({ status:'unchanged', incoming, existing, errors: [] });
  });

  // Detect missing: active DB staff at a scoped store, not matched by any incoming row
  const matchedIds = new Set(staffDiffResult.filter(r => r.existing).map(r => r.existing.id));
  staffData.forEach(s => {
    if (s.active === false) return;
    if (!scopedAppStores.has(s.store)) return;
    if (matchedIds.has(s.id)) return;
    staffDiffResult.push({ status:'missing', incoming: null, existing: s, errors: [] });
  });

  // v54: Second pass — for every NEW row, find moderate-confidence candidate matches
  // among the unmatched MISSING rows. Surface as suggestions, NOT auto-merges.
  staffDiffResult.forEach(r => {
    if (r.status !== 'add') return;
    const key = r.incoming.empCode || r.incoming.fullName;
    if (window._amanahDismissedSuggestions[key]) return;
    const suggestions = findSuggestedMatches(r.incoming, matchedIds);
    if (suggestions.length) r.suggestedMatches = suggestions;
  });

  const counts = { add:0, change:0, missing:0, unchanged:0, error:0 };
  staffDiffResult.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });

  const setBadge = (id, label, count) => {
    const el = document.getElementById(id);
    if (el) { el.textContent = count + ' ' + label; el.style.display = count ? '' : 'none'; }
  };
  setBadge('diffBadgeAdd',       'new',       counts.add);
  setBadge('diffBadgeChange',    'changed',   counts.change);
  setBadge('diffBadgeMissing',   'missing',   counts.missing);
  setBadge('diffBadgeUnchanged', 'unchanged', counts.unchanged);
  setBadge('diffBadgeError',     'errors',    counts.error);

  ['add','change','missing','unchanged','error'].forEach(k => {
    const btn = document.getElementById('diffFilterBtn-' + k);
    if (btn) btn.style.display = counts[k] ? '' : 'none';
  });

  const scopeInfo = document.getElementById('diffScopeInfo');
  if (scopeInfo) {
    const uaeCount = [...scopedAppStores].filter(s => !s.startsWith('Doha')).length;
    const qaCount  = [...scopedAppStores].filter(s =>  s.startsWith('Doha')).length;
    const outOfScope = staffData.filter(s => s.active !== false && !scopedAppStores.has(s.store)).length;
    const pendingSug = staffDiffResult.filter(r => r.status === 'add' && r.suggestedMatches && r.suggestedMatches.length).length;
    let msg = `📍 <strong>Scope:</strong> this file covers ${scopedAppStores.size} app stores (${uaeCount} UAE, ${qaCount} Qatar). Missing-check applies to those stores only. <strong>${outOfScope} active staff</strong> at other stores are out of scope and won't be touched.`;
    if (pendingSug) msg += ` <span style="color:var(--warn-deep)">💡 ${pendingSug} possible match${pendingSug>1?'es':''} to review in the New tab.</span>`;
    scopeInfo.innerHTML = msg;
  }

  updateApplyButtonState();
  document.getElementById('staffDiffCard').style.display = '';
  renderDiffTable(diffCurrentFilter);
}

export function renderDiffTable(filter) {
  diffCurrentFilter = filter;
  ['all','add','change','missing','unchanged','error'].forEach(f => {
    const btn = document.getElementById('diffFilterBtn-' + f);
    if (btn) btn.className = 'btn btn-sm ' + (f === filter ? 'btn-green' : 'btn-outline');
  });

  const rows = filter === 'all' ? staffDiffResult : staffDiffResult.filter(r => r.status === filter);
  if (!rows.length) {
    setHTML('staffDiffTable', '<div style="text-align:center;padding:20px;color:var(--sub)">No records in this category.</div>');   // v136.8: null-safe (the box may not be on screen)
    return;
  }

  const statusLabel = {
    add:       '<span class="badge badge-green">NEW</span>',
    change:    '<span class="badge badge-amber">CHANGED</span>',
    missing:   '<span class="badge badge-red">MISSING</span>',
    unchanged: '<span class="badge badge-grey">OK</span>',
    error:     '<span class="badge badge-red">ERROR</span>',
  };

  let html = '<table class="data-table" style="font-size:12px"><thead><tr><th>Status</th><th>Emp Code</th><th>Name</th><th>Role</th><th>Store</th><th>Notice</th><th>Leave (sick · annual remaining)</th><th>Details</th></tr></thead><tbody>';

  rows.forEach(r => {
    const rowStyle =
      r.status === 'error'   ? 'background:var(--adm-danger-pale)' :
      r.status === 'add'     ? 'background:var(--adm-ok-pale)' :
      r.status === 'change'  ? 'background:var(--adm-warn-pale)' :
      r.status === 'missing' ? 'background:var(--adm-danger-pale)' : '';

    let empCode = '—', name = '', role = '—', store = '—', onNotice = '—', details = '', leaveCell = '—';

    if (r.status === 'missing') {
      const s = r.existing;
      empCode = s.empCode || '—';
      name = `<span translate="no">${escText(s.name)}</span>`;   // v135.2: names and stores stay as written in Arabic; v136.8: escaped
      role = s.role || '—';
      store = s.store ? `<span translate="no">${escText(s.store)}</span>` : '—';
      onNotice = s.onNotice ? '⚠' : '—';
      details = '<span style="color:var(--red);font-size:11px">At a file-scoped store, but not in file. Suggested: archive.</span>';
      leaveCell = '<span style="color:var(--sub);font-size:11px">n/a — not in file</span>';
      // v54: symmetric hint — if any NEW row in the diff has this staff as a suggestion,
      // surface it here too so Matt sees it whether he opens Missing or New tab first.
      const pointingNew = staffDiffResult.find(x =>
        x.status === 'add' && Array.isArray(x.suggestedMatches) &&
        x.suggestedMatches.some(sm => sm.existing && sm.existing.id === s.id)
      );
      if (pointingNew) {
        const top = pointingNew.suggestedMatches.find(sm => sm.existing.id === s.id);
        const conf = top.confidence === 'strong' ? 'likely the same person' : 'possibly the same person';
        const confColor = top.confidence === 'strong' ? 'var(--ok-deep)' : 'var(--warn-deep)';
        const bg = top.confidence === 'strong' ? 'var(--ok-bg)' : 'var(--warn-bg)';
        const empCodeSafe = jsArg(pointingNew.incoming.empCode);   // v136.8 (M3)
        details += `<div style="margin-top:6px;padding:6px 8px;border-radius:6px;background:${bg};color:${confColor};font-size:11px">`;
        details += `💡 <strong>${conf}</strong> as <strong translate="no">${escText(pointingNew.incoming.fullName)}</strong> (in file as <span style="background:var(--ok-bg);color:var(--ok-deep);padding:1px 5px;border-radius:3px;font-weight:600;font-size:10px">NEW ${escText(pointingNew.incoming.empCode)}</span>)`;
        details += `<div style="margin-top:4px">`;
        details += `<button class="btn btn-sm btn-green" style="padding:2px 8px;font-size:11px" onclick="Uploads.staff.amanahAcceptMatch('${empCodeSafe}',${Number(s.id)})">Match</button> `;
        details += `<button class="btn btn-sm btn-outline" style="padding:2px 8px;font-size:11px" onclick="Uploads.staff.amanahDismissSuggestion('${empCodeSafe}')">Keep separate</button>`;
        details += `</div></div>`;
      }
    } else {
      const inc = r.incoming;
      empCode = inc.empCode || '—';
      name = `<span translate="no">${escText(inc.fullName)}</span>`;   // v136.8 (M3): escaped
      // v52: append a small confidence chip if AI flagged this row as low-confidence
      if (inc.confidence === 'low') {
        name += ' <span style="background:var(--warn-bg);color:var(--warn-deep);font-size:10px;padding:1px 6px;border-radius:4px;font-weight:500;margin-inline-start:4px">low-conf</span>';
      }
      role = inc.role || '—';
      store = inc.appStore ? `<span translate="no">${escText(inc.appStore)}</span>` : `<span style="color:var(--red)"><span translate="no">${escText(inc.amanahStore)}</span> (unmapped)</span>`;
      onNotice = inc.onNotice ? '⚠' : '—';

      // v58: render whichever leave values we have. New format: both sick + annual are "remaining".
      // Legacy format: sick is total entitlement, annual is remaining.
      const sickRem = (typeof inc.sickLeaveRemaining === 'number') ? inc.sickLeaveRemaining : null;
      const sickTot = (typeof inc.sickLeaveDays === 'number') ? inc.sickLeaveDays : null;
      const annRem  = (typeof inc.annualLeaveRemaining === 'number') ? inc.annualLeaveRemaining : null;
      if (sickRem !== null || sickTot !== null || annRem !== null) {
        let sickStr;
        if (sickRem !== null) {
          sickStr = sickRem < 0 ? `<span style="color:var(--red)">${sickRem}d left (overused)</span>` : sickRem + 'd left';
        } else if (sickTot !== null) {
          sickStr = sickTot + 'd total';
        } else {
          sickStr = '—';
        }
        const annStr = annRem !== null
          ? (annRem < 0 ? `<span style="color:var(--red)">${annRem}d (overused)</span>` : annRem + 'd left')
          : '—';
        // v135.2: each value in its own span, so '5d left' is one translatable piece
        leaveCell = `<span style="font-size:11px">🏥 <span>${sickStr}</span> · 🏖 <span>${annStr}</span></span>`;
      } else {
        leaveCell = '<span style="color:var(--sub);font-size:11px">—</span>';
      }

      if (r.status === 'error') {
        details = r.errors.map(e => `<div style="color:var(--red);font-size:11px">⚠️ ${escText(e)}</div>`).join('');
      } else if (r.status === 'change') {
        details = r.changed.map(c => `<div style="font-size:11px"><span translate="no" style="color:var(--red);text-decoration:line-through">${escText(c.from || '—')}</span> → <span translate="no" style="color:var(--green);font-weight:600">${escText(c.to || '—')}</span> <span style="color:var(--sub)">(${escText(c.field)})</span></div>`).join('');
        // v54: If this change came from a manual match, show an Undo link
        const manualId = window._amanahManualMatches && window._amanahManualMatches[inc.empCode];
        if (manualId && r.existing && r.existing.id === manualId) {
          details += `<div style="margin-top:4px;font-size:10px;color:var(--sub)">🔗 Manually matched · <a href="#" onclick="event.preventDefault();Uploads.staff.amanahUndoMatch('${jsArg(inc.empCode)}')" style="color:var(--red)">undo</a></div>`;
        }
      } else if (r.status === 'add') {
        details = '<span style="color:var(--green);font-size:11px">Will be added as new staff member</span>';
        // v54: surface fuzzy-match suggestions so the user can merge rather than add+archive
        if (Array.isArray(r.suggestedMatches) && r.suggestedMatches.length) {
          const top = r.suggestedMatches[0];
          const conf = top.confidence === 'strong' ? 'likely the same person' : 'possibly the same person';
          const confColor = top.confidence === 'strong' ? 'var(--ok-deep)' : 'var(--warn-deep)';
          const bg = top.confidence === 'strong' ? 'var(--ok-bg)' : 'var(--warn-bg)';
          const empCodeSafe = jsArg(inc.empCode);   // v136.8 (M3)
          details += `<div style="margin-top:6px;padding:6px 8px;border-radius:6px;background:${bg};color:${confColor};font-size:11px">`;
          details += `💡 <strong>${conf}</strong> as <strong translate="no">${escText(top.existing.name)}</strong> (currently <span style="background:var(--danger-bg);color:var(--danger-deep);padding:1px 5px;border-radius:3px;font-weight:600;font-size:10px">MISSING</span>)`;
          if (r.suggestedMatches.length > 1) details += ` <span style="color:var(--sub)">+${r.suggestedMatches.length - 1} other</span>`;
          details += `<div style="margin-top:4px">`;
          details += `<button class="btn btn-sm btn-green" style="padding:2px 8px;font-size:11px" onclick="Uploads.staff.amanahAcceptMatch('${empCodeSafe}',${Number(top.existing.id)})">Match</button> `;
          details += `<button class="btn btn-sm btn-outline" style="padding:2px 8px;font-size:11px" onclick="Uploads.staff.amanahDismissSuggestion('${empCodeSafe}')">Keep separate</button>`;
          details += `</div></div>`;
        }
      } else {
        details = '<span style="color:var(--sub);font-size:11px">No changes</span>';
      }

      // v52: surface AI-reported row-level errors (e.g. unrecognised designation)
      if (Array.isArray(inc.aiErrors) && inc.aiErrors.length) {
        details += inc.aiErrors.map(e => `<div style="color:var(--amber);font-size:11px;margin-top:2px">ℹ️ ${escText(e)}</div>`).join('');
      }
    }

    html += `<tr style="${rowStyle}">
      <td>${statusLabel[r.status]}</td>
      <td style="font-family:monospace;font-size:11px">${escText(empCode)}</td>
      <td style="font-weight:600">${name}</td>
      <td>${escText(role)}</td>
      <td style="font-size:11px">${store}</td>
      <td style="text-align:center">${onNotice}</td>
      <td>${leaveCell}</td>
      <td>${details}</td>
    </tr>`;
  });

  html += '</tbody></table>';
  setHTML('staffDiffTable', html);   // v136.8: null-safe (the box may not be on screen)
}


// ════════════════════════════════════════════════════════════
// v99.0 additions — File parsing pipeline (closes the staff arc)
// ════════════════════════════════════════════════════════════
// The Amanah HR file parsing pipeline moved into this module. After
// v99, the entire staff bulk upload flow (file-drop → parse → store
// mapping → diff → apply) lives in Uploads.staff. admin.html only
// owns the file-input HTML markup + the ondrop/onchange wiring.
//
// Five functions, ~388 LOC moved:
//   • handleStaffFileDrop  — drop event router
//   • handleStaffFiles     — async orchestrator (parse → merge →
//                            map → render diff)
//   • readFileToRows       — xlsx/csv → 2D array
//   • parseFileViaStrict   — schema-strict header-based parser
//                            (Amanah .xlsx format)
//   • parseFileViaAi       — AI fallback parser when strict fails
//                            (uses Claude via DB.config worker route)
//
// External callers (after v99):
//   • admin.html HTML markup:
//       ondrop="Uploads.staff.handleStaffFileDrop(event)"
//       onchange="Uploads.staff.handleStaffFiles(this.files)"
//
// State vars (already var-declared from earlier sprints):
//   • staffUploadParsed     (written by handleStaffFiles)
//   • staffStoreMapping     (reset by handleStaffFiles)
//
// Free globals consumed:
//   • ALL_STORES            — canonical store list
//   • staffData             — current staff cache
//   • DB                    — db.js facade (DB.config.get/set for AI key,
//                              DB.balances writes after apply)
//   • XLSX                  — SheetJS global (xlsx parsing)
//   • parseNum              — admin.html helper
//   • showToast             — toast helper
//
// Match utilities (Uploads.match.X) are referenced directly via the
// global Uploads namespace — works at runtime since the bundle is loaded
// before any inline code runs.

export function handleStaffFileDrop(e) {
  e.preventDefault();
  const dz = document.getElementById('staffUploadDropZone');
  if (dz) { dz.style.borderColor = 'var(--border)'; dz.style.background = 'var(--surface-2)'; }
  const files = e.dataTransfer && e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
  if (files.length) handleStaffFiles(files);
}

export function readFileToRows(file) {
  // v136.8: an .xlsx needs the spreadsheet library, which loads on first use (a failed load rejects with a plain message).
  const isXlsx = file.name.split('.').pop().toLowerCase() === 'xlsx';
  return (isXlsx ? ensureXlsx() : Promise.resolve()).then(() => new Promise((resolve, reject) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = e => {
      try {
        let rows;
        if (ext === 'xlsx') {
          const wb = XLSX.read(new Uint8Array(e.target.result), {type:'array'});
          const ws = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws, {header:1, raw:false, defval:''});
        } else {
          const text = new TextDecoder('utf-8').decode(e.target.result);
          rows = text.split(/\r?\n/).map(l => l.split(',').map(c => c.trim().replace(/^"|"$/g,'')));
        }
        resolve(rows);
      } catch(err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Could not read ' + file.name));
    reader.readAsArrayBuffer(file);
  }));
}

export function parseFileViaStrict(rows, label) {
  // Find header row — try new format first, fall back to legacy
  let headerRowIdx = -1;
  let formatVersion = null;
  for (let i = 0; i < Math.min(rows.length, 5); i++) {
    const norms = (rows[i] || []).map(c => Uploads.match.normAmanahName(c));
    // New format detection: "emp code" AND "staff name(s)" [normalised as "staff names"]
    if (norms.indexOf('emp code') !== -1 && norms.some(n => n.startsWith('staff name'))) {
      headerRowIdx = i; formatVersion = 'v58'; break;
    }
    // Legacy format: "first name" present
    if (norms.indexOf('first name') !== -1) {
      headerRowIdx = i; formatVersion = 'legacy'; break;
    }
  }
  if (headerRowIdx < 0) {
    return { label, rows: [], warnings: ['could not find header row in ' + label + ' — expected "Emp Code" + "Staff name(s)" columns'], sourceRowCount: 0 };
  }

  const headers = rows[headerRowIdx].map(h => Uploads.match.normAmanahName(h));
  const cell = (row, c) => c >= 0 ? String(row[c] ?? '').trim() : '';

  if (formatVersion === 'v58') {
    // New format
    const idx = {
      empCode:     headers.indexOf('emp code'),
      staffName:   headers.findIndex(h => h.startsWith('staff name')), // "staff name" or "staff names" (both match)
      position:    headers.indexOf('position'),
      retailStore: headers.indexOf('retail store'),
      // Leave columns — names have typos in the wild ("Annal" / trailing spaces)
      // normAmanahName collapses whitespace; we still need flexible matching.
      annualRemaining: headers.findIndex(h => h.includes('remaining') && (h.includes('annal') || h.includes('annual'))),
      sickLeave:       headers.findIndex(h => h.startsWith('sick leave') || h === 'sick' || h.startsWith('sick ')),
    };
    // Required columns
    const required = ['empCode','staffName','position','retailStore'];
    const missing = required.filter(k => idx[k] < 0);
    if (missing.length) {
      return { label, rows: [], warnings: ['missing required columns in ' + label + ': ' + missing.join(', ')], sourceRowCount: 0 };
    }

    const dataRows = rows.slice(headerRowIdx + 1).filter(r => r.some(c => String(c).trim()));
    const out = [];
    const warnings = [];
    let skippedNoEmpCode = 0;

    dataRows.forEach((row, i) => {
      const empCode   = cell(row, idx.empCode).toUpperCase();
      const fullRaw   = cell(row, idx.staffName);
      const position  = cell(row, idx.position);
      const amanahStore = cell(row, idx.retailStore);

      if (!fullRaw) return; // blank row
      if (!empCode) {
        skippedNoEmpCode++;
        return; // v58: emp_code is the primary tracker; rows without it can't be ingested
      }

      // Split combined name into first + rest (app stores firstName+lastName separately)
      const fullClean = Uploads.match.titleCaseName(fullRaw);
      const tokens = fullClean.split(/\s+/).filter(Boolean);
      const firstName = tokens[0] || '';
      const lastName  = tokens.slice(1).join(' ') || '';

      // Leave values — parse numeric, allow negatives, null if blank/invalid
      const parseNum = (s) => {
        const t = (s || '').trim();
        if (t === '' || t === '-') return null;
        const n = parseFloat(t);
        return isNaN(n) ? null : n;
      };
      const annualLeaveRemaining = idx.annualRemaining >= 0 ? parseNum(cell(row, idx.annualRemaining)) : null;
      const sickLeaveRemaining   = idx.sickLeave       >= 0 ? parseNum(cell(row, idx.sickLeave))       : null;

      out.push({
        firstName, lastName,
        fullName: fullClean,
        empCode, designation: position,
        role: Uploads.match.designationToRole(position),
        amanahStore, appStore: Uploads.match.mapAmanahStore(amanahStore),
        onNotice: false, // new format doesn't carry this; admin UI can flag manually
        // v58 leave semantics — BOTH values are "days remaining" in this file format
        annualLeaveRemaining,
        sickLeaveRemaining,
        // Legacy field kept null — old format's sickLeaveDays was "total entitlement"
        sickLeaveDays: null,
        confidence: 'high', aiErrors: [],
        _row: i + headerRowIdx + 2,
      });
    });

    if (skippedNoEmpCode > 0) {
      warnings.push(skippedNoEmpCode + ' row(s) skipped — no Emp Code. Emp Code is required for every row.');
    }

    return { label, rows: out, warnings, sourceRowCount: dataRows.length, formatVersion };
  }

  // Legacy format fallback (pre-2026-04-23 exports) — kept for back-compat
  const idx = {
    firstName:   headers.indexOf('first name'),
    lastName:    headers.indexOf('last name'),
    empCode:     headers.indexOf('emp code'),
    designation: headers.indexOf('designation'),
    retailStore: headers.indexOf('retail store'),
  };
  const missing = Object.entries(idx).filter(([,v]) => v < 0).map(([k]) => k);
  if (missing.length) {
    return { label, rows: [], warnings: ['missing columns in ' + label + ': ' + missing.join(', ')], sourceRowCount: 0 };
  }
  const statusColIdx = idx.retailStore + 1;
  const dataRows = rows.slice(headerRowIdx + 1).filter(r => r.some(c => String(c).trim()));
  const warnings = [];
  let skippedNoEmpCode = 0;
  const out = [];
  dataRows.forEach((row, i) => {
    const firstName = Uploads.match.titleCaseName(cell(row, idx.firstName));
    const lastName  = Uploads.match.titleCaseName(cell(row, idx.lastName));
    const empCode   = cell(row, idx.empCode).toUpperCase();
    if (!(firstName || lastName)) return; // blank row
    if (!empCode) { skippedNoEmpCode++; return; }
    const designation = cell(row, idx.designation);
    const amanahStore = cell(row, idx.retailStore);
    const statusNote  = cell(row, statusColIdx).toLowerCase();
    const onNotice    = statusNote.includes('resign') || statusNote.includes('notice');
    out.push({
      firstName, lastName,
      fullName: (firstName + ' ' + lastName).trim(),
      empCode, designation,
      role: Uploads.match.designationToRole(designation),
      amanahStore, appStore: Uploads.match.mapAmanahStore(amanahStore),
      onNotice,
      annualLeaveRemaining: null,
      sickLeaveRemaining: null,
      sickLeaveDays: null,
      confidence: 'high', aiErrors: [],
      _row: i + headerRowIdx + 2,
    });
  });
  if (skippedNoEmpCode > 0) {
    warnings.push(skippedNoEmpCode + ' row(s) skipped — no Emp Code. Emp Code is required for every row.');
  }
  return { label, rows: out, warnings, sourceRowCount: dataRows.length, formatVersion: 'legacy' };
}

export async function parseFileViaAi(rawRows, label) {
  // v58: header detection — match BOTH new format ("staff name(s)" + "emp code")
  // and legacy format ("first name")
  let headerIdx = -1;
  for (let i = 0; i < Math.min(rawRows.length, 10); i++) {
    const norms = (rawRows[i] || []).map(c => Uploads.match.normAmanahName(c));
    if ((norms.indexOf('emp code') !== -1 && norms.some(n => n.startsWith('staff name'))) ||
        norms.indexOf('first name') !== -1) {
      headerIdx = i; break;
    }
  }
  const rawDataRowCount = headerIdx >= 0
    ? rawRows.slice(headerIdx + 1).filter(r => r && r.some(c => String(c).trim())).length
    : rawRows.filter(r => r && r.some(c => String(c).trim())).length;

  const storeSet = new Set(ALL_STORES);
  staffData.forEach(s => { if (s.store) storeSet.add(s.store); });
  const knownStores = [...storeSet].sort();

  // Call AI
  let parsed;
  try {
    const resp = await DB.ai.normaliseStaff({ rows: rawRows, knownStores, knownRoles: Uploads.match.AMANAH_VALID_ROLES });
    const text = (resp && resp.content && resp.content[0] && resp.content[0].text) || '';
    if (!text) throw new Error('empty response');
    let clean = text.trim().replace(/^```json\s*/i,'').replace(/^```\s*/,'').replace(/\s*```$/,'').trim();
    if (!clean.startsWith('{')) {
      const si = clean.indexOf('{'), ei = clean.lastIndexOf('}');
      if (si !== -1 && ei !== -1) clean = clean.slice(si, ei+1);
    }
    parsed = JSON.parse(clean);
  } catch(e) {
    console.warn('[amanah] AI normalisation failed for ' + label + ':', e.message);
    const strict = parseFileViaStrict(rawRows, label);
    strict.warnings.unshift('AI unavailable — fell back to strict parsing');
    return strict;
  }

  // Validate AI output. Any schema failure → fall back to strict for just this file.
  const fallback = (reason) => {
    console.warn('[amanah] ' + label + ' AI output rejected:', reason);
    const strict = parseFileViaStrict(rawRows, label);
    strict.warnings.unshift('AI output failed sanity check (' + reason + ') — fell back to strict parsing');
    return strict;
  };
  if (!parsed || typeof parsed !== 'object')       return fallback('not an object');
  if (!Array.isArray(parsed.staff))                return fallback('staff[] missing');
  if (rawDataRowCount > 0 && parsed.staff.length < rawDataRowCount - 1) {
    return fallback('row-count mismatch — file has ' + rawDataRowCount + ', AI returned ' + parsed.staff.length);
  }
  const EMP_CODE_RE = /^[A-Z]{2,4}\d{3,5}$/;
  const knownStoresSet = new Set(knownStores);
  for (let i = 0; i < parsed.staff.length; i++) {
    const r = parsed.staff[i];
    if (!r || typeof r !== 'object')                            return fallback('row ' + i + ' not an object');
    if (typeof r.firstName !== 'string' || !r.firstName.trim()) return fallback('row ' + i + ' missing firstName');
    if (typeof r.empCode !== 'string'  || !EMP_CODE_RE.test(r.empCode)) return fallback('row ' + i + ' invalid empCode "' + r.empCode + '"');
    if (r.appStore != null && !knownStoresSet.has(r.appStore))  return fallback('row ' + i + ' unknown appStore "' + r.appStore + '"');
  }

  // Shape into the expected row structure
  // v58: new leave field `sickLeaveRemaining` sits alongside the legacy `sickLeaveDays`.
  // AI returns whichever it could determine from the column header semantics.
  const out = parsed.staff.map((r, i) => ({
    firstName: Uploads.match.titleCaseName(r.firstName || ''),
    lastName:  Uploads.match.titleCaseName(r.lastName || ''),
    fullName:  ((Uploads.match.titleCaseName(r.firstName || '')) + ' ' + (Uploads.match.titleCaseName(r.lastName || ''))).trim(),
    empCode:   String(r.empCode || '').toUpperCase(),
    designation: String(r.designation || ''),
    role:      r.role || Uploads.match.designationToRole(r.designation || ''),
    amanahStore: String(r.amanahStore || '').trim(),
    appStore:  r.appStore || null,
    onNotice:  !!r.onNotice,
    // v58: new field (days remaining) — preferred for the new file format.
    sickLeaveRemaining:   (typeof r.sickLeaveRemaining === 'number') ? r.sickLeaveRemaining : null,
    // Legacy field (total entitlement) — preferred for the old file format.
    sickLeaveDays:        (typeof r.sickLeaveDays === 'number') ? r.sickLeaveDays : null,
    annualLeaveRemaining: (typeof r.annualLeaveRemaining === 'number') ? r.annualLeaveRemaining : null,
    confidence: r.confidence || 'high',
    aiErrors:  Array.isArray(r.errors) ? r.errors : [],
    _row: i + (headerIdx >= 0 ? headerIdx + 2 : 2),
  })).filter(r => r.fullName && r.empCode);

  return {
    label,
    rows: out,
    warnings: Array.isArray(parsed.warnings) ? parsed.warnings : [],
    sourceRowCount: rawDataRowCount,
  };
}

export async function handleStaffFiles(filesLike) {
  const files = Array.from(filesLike || []);
  if (!files.length) return;

  // v54: clear any manual-match / dismissed-suggestion state from a previous upload
  // so stale pairings don't bleed into a new diff
  window._amanahManualMatches = {};
  window._amanahDismissedSuggestions = {};

  // Validate extensions first so we fail early on a bad drop
  for (const f of files) {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx','csv'].includes(ext)) {
      Uploads.staff.showStatus('error', '❌ "' + f.name + '" is not .xlsx or .csv — drop Amanah exports only.');
      return;
    }
  }

  const multi = files.length > 1;
  Uploads.staff.showStatus('info', multi
    ? `⏳ Reading ${files.length} files…`
    : '⏳ Reading file…');

  // Read all files to rawRows (parallel)
  let allFileRows;
  try {
    allFileRows = await Promise.all(files.map(readFileToRows));
  } catch(err) {
    Uploads.staff.showStatus('error', '❌ Could not read file: ' + err.message);
    return;
  }

  // Decide parser path: AI (default) or strict
  const smart = document.getElementById('amanahSmartParse');
  const useAi = !(smart && !smart.checked);

  if (multi) {
    Uploads.staff.showStatus('info', useAi
      ? `🤖 Smart parsing ${files.length} files in parallel… (5–15s)`
      : `⏳ Parsing ${files.length} files…`);
  } else if (useAi) {
    Uploads.staff.showStatus('info', '🤖 Reading file with smart parsing… (3–10s)');
  }

  // Parse each file independently — each returns { rows: [...parsedStaffRows], warnings, label, sourceRowCount }
  let results;
  try {
    results = await Promise.all(allFileRows.map((rawRows, i) =>
      useAi
        ? parseFileViaAi(rawRows, files[i].name)
        : Promise.resolve(parseFileViaStrict(rawRows, files[i].name))
    ));
  } catch(err) {
    Uploads.staff.showStatus('error', '❌ Parse failed: ' + err.message);
    return;
  }

  // Merge + dedupe by empCode
  const merged = [];
  const byEmpCode = {};        // empCode → index into merged
  const duplicates = [];       // {empCode, files: [...]}
  const allWarnings = [];
  let totalSourceRows = 0;

  results.forEach((res, fi) => {
    if (!res) return;
    if (Array.isArray(res.warnings)) {
      res.warnings.forEach(w => allWarnings.push('[' + res.label + '] ' + w));
    }
    totalSourceRows += res.sourceRowCount || 0;
    (res.rows || []).forEach(row => {
      row._sourceFile = res.label;
      const code = row.empCode;
      if (code && byEmpCode[code] !== undefined) {
        // Same emp appears in two files. Keep the first, flag the conflict.
        const existing = merged[byEmpCode[code]];
        const dup = duplicates.find(d => d.empCode === code);
        if (dup) {
          if (dup.files.indexOf(res.label) === -1) dup.files.push(res.label);
        } else {
          duplicates.push({ empCode: code, name: existing.fullName || row.fullName, files: [existing._sourceFile, res.label] });
        }
        return;
      }
      byEmpCode[code] = merged.length;
      merged.push(row);
    });
  });

  if (!merged.length) {
    Uploads.staff.showStatus('error', '❌ No valid rows across ' + files.length + ' file(s). Try turning off smart parsing to see the raw issue.');
    return;
  }

  // Finalize: this mirrors the tail of the original single-file flow.
  staffUploadParsed = merged;

  const uniqueAmanahStores = [...new Set(staffUploadParsed.map(r => r.amanahStore))].filter(Boolean).sort();
  staffStoreMapping = {};
  uniqueAmanahStores.forEach(a => {
    const firstMatch = staffUploadParsed.find(r => r.amanahStore === a);
    staffStoreMapping[a] = (firstMatch && firstMatch.appStore) || Uploads.match.mapAmanahStore(a);
  });

  Uploads.staff.renderStoreMappingCard();
  Uploads.staff.buildAmanahDiff();

  const mapped = Object.values(staffStoreMapping).filter(Boolean).length;
  const lowConf = staffUploadParsed.filter(r => r.confidence === 'low').length;

  let msg = multi
    ? `✅ ${useAi ? 'Smart parsing' : 'Strict parsing'} ${files.length} files: ${staffUploadParsed.length} staff across ${uniqueAmanahStores.length} stores (${mapped} auto-mapped)`
    : `✅ ${useAi ? 'Smart parsing' : 'Parsed'}: ${staffUploadParsed.length} staff across ${uniqueAmanahStores.length} stores (${mapped} auto-mapped)`;
  if (lowConf) msg += ` · ${lowConf} low-confidence row(s)`;
  if (duplicates.length) msg += ` · ⚠ ${duplicates.length} duplicate emp code(s) across files — kept first instance`;
  if (allWarnings.length) msg += ` · ${allWarnings.length} note(s) from AI`;
  msg += '. Review below.';
  Uploads.staff.showStatus(duplicates.length ? 'info' : 'success', msg);

  if (duplicates.length) {
    console.warn('[amanah] Duplicate emp codes across files:');
    console.table(duplicates);
  }
  if (allWarnings.length) console.log('[amanah] AI warnings:', allWarnings);
}


// ════════════════════════════════════════════════════════════
// v101.0 additions — Staff directory + CRUD (people page)
// ════════════════════════════════════════════════════════════
// Six more functions joining Uploads.staff. Combined with v95+v98+v99,
// Uploads.staff is now the entire Staff page surface — bulk upload,
// individual CRUD, directory rendering.
//
//   • renderStaff               — directory list refresh
//   • renderStaffGrouped        — grouped-by-store view
//   • renderStaffTable          — flat table view
//   • renderStaffConsentStatus  — per-staff consent dot/badge
//   • saveStaff (async)         — form submit handler (create + update)
//   • archiveStaff              — soft-delete via active=false
//
// Internal calls within these functions (renderStaffGrouped from
// renderStaff, etc.) become module-local bare references after the
// move. Defensive `typeof renderStaff === 'function'` guards in
// admin.html become silent landmines once the bare name is gone —
// the v97-banked lesson applied here, replacing them with direct
// `Uploads.staff.renderStaff()` calls.

export function renderStaffConsentStatus(s) {
  const statusEl = document.getElementById('sfConsentStatus');
  const btn = document.getElementById('sfResetConsentBtn');
  if (!statusEl || !btn) return;
  const ackedThisVer = s && s.consent_version === (typeof CONSENT_VERSION !== 'undefined' ? CONSENT_VERSION : 'v1');
  if (ackedThisVer) {
    let when = '';
    if (s.consent_acknowledged_at) {
      try {
        const d = new Date(s.consent_acknowledged_at);
        when = ' on ' + d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch(e) {}
    }
    const lang = s.consent_lang ? ' (' + s.consent_lang.toUpperCase() + ')' : '';
    statusEl.innerHTML = '<span style="color:var(--green)">✅ Acknowledged' + escapeHtml(when) + escapeHtml(lang) + '</span>';
    btn.style.display = '';
  } else {
    statusEl.innerHTML = '<span style="color:#f59e0b">🟠 Not yet acknowledged</span>';
    btn.style.display = 'none';
  }
}

// The Amanah import writes 'store_manager' / 'store in-charge' (src/uploads/match.js
// designationToRole); older rows and the edit form say 'Store Manager' /
// 'Store in-charge'. Compare case-insensitively against both spellings.
function roleKey(role) { return String(role || '').toLowerCase().replace(/_/g, ' ').trim(); }
function isStoreManagerRole(role) { return roleKey(role) === 'store manager'; }
function isStoreLeadRole(role) {
  const k = roleKey(role);
  return k === 'store manager' || k === 'store in-charge' || k === 'store in charge';
}

export function renderStaffTable(filtered, container) {
  if (typeof StaffRoles !== 'undefined' && StaffRoles.ensureActing) StaffRoles.ensureActing();   // v133.52: acting-role chips, fetched once
  const today = todayDateStr();
  let rows = filtered.map(s => {
    const pending = changeLog.find(c => c.staffId === s.id && !c.appliedAt && c.effectiveFrom > today);
    const color = s.color || COLORS[s.id % COLORS.length];
    const initials = s.name.split(' ').filter(Boolean).map(w=>w[0]).join('').slice(0,2).toUpperCase();
    const roleClass = isStoreManagerRole(s.role)?'badge-green':'badge-grey';
    const canEdit = canEditStaff(s);
    // v59: privacy-notice consent state
    const ackOk = s.consent_version === CONSENT_VERSION;
    const ackCell = ackOk
      ? '<span style="color:var(--ok);font-size:11px" title="' + (s.consent_acknowledged_at ? 'Acknowledged ' + new Date(s.consent_acknowledged_at).toLocaleDateString() : 'Acknowledged') + '">✓</span>'
      : '<span style="color:#f59e0b;font-size:11px" title="Privacy notice not yet acknowledged">—</span>';
    return `<tr>
      <td><div style="display:flex;align-items:center;gap:8px">
        <div translate="no" style="width:28px;height:28px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;flex-shrink:0">${initials}</div>
        <div><div translate="no" style="font-weight:600">${escapeHtml(s.name||'')}</div>${pending?'<div style="font-size:10px;color:var(--warn-deep)">Change scheduled '+pending.effectiveFrom+'</div>':''}</div>
      </div></td>
      <td><span class="badge ${roleClass}">${escapeHtml(s.role||'')}</span>${typeof StaffRoles !== 'undefined' && StaffRoles.actingChip ? StaffRoles.actingChip(s.id) : ''}</td>
      <td translate="no">${escapeHtml(s.store||'')}</td>
      <td translate="no">${escapeHtml(getAreaNameById(s.area)||'')}</td>
      <td>${escapeHtml(s.dayOff||'—')}</td>
      <td style="text-align:center">${ackCell}</td>
      <td>${canEdit ? '<button class="btn btn-outline btn-sm" onclick="Uploads.staff.openStaffModal('+s.id+')">Edit</button>' : '—'}</td>
    </tr>`;
  }).join('');
  container.innerHTML = `<div class="card" style="padding:0;overflow:hidden"><div class="table-wrap">
    <table class="data-table">
      <thead><tr><th>Name</th><th>Role</th><th>Store</th><th>Area</th><th>Day Off</th><th title="Privacy notice acknowledged">Notice</th><th>Actions</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div></div>`;
}

export function renderStaffGrouped(filtered, container) {
  // Group by store
  const byStore = {};
  filtered.forEach(s => {
    if (!byStore[s.store]) byStore[s.store] = [];
    byStore[s.store].push(s);
  });

  const today = todayDateStr();

  container.innerHTML = '';
  Object.entries(byStore).sort(([a],[b]) => a.localeCompare(b)).forEach(([store, members]) => {
    const mgr = members.find(s => isStoreManagerRole(s.role)) || members.find(s => isStoreLeadRole(s.role));
    const area = getAreaNameById(members[0]?.area || '');
    const group = document.createElement('div');
    group.className = 'store-group';

    const pendingInStore = changeLog.filter(c => !c.appliedAt && c.effectiveFrom > today &&
      members.some(m => m.id === c.staffId)).length;

    group.innerHTML = `
      <div class="store-group-header">
        <div>
          <div class="store-group-title" translate="no">${escapeHtml(store)}</div>
          <div class="store-group-meta"><span translate="no">${escapeHtml(area)}</span> area · ${mgr ? 'SM: <span translate="no">' + escapeHtml(String(mgr.name || '').split(' ')[0]) + '</span>' : 'No SM assigned'} · ${members.length} staff</div>
        </div>
        ${pendingInStore ? '<span class="badge badge-amber">'+pendingInStore+' pending change'+(pendingInStore>1?'s':'')+'</span>' : ''}
      </div>
      <div class="store-group-body">
        ${members.sort((a,b) => {
          if (isStoreManagerRole(a.role)) return -1;
          if (isStoreManagerRole(b.role)) return 1;
          return a.name.localeCompare(b.name);
        }).map(s => staffRowHtml(s, today)).join('')}
      </div>
    `;
    container.appendChild(group);
  });

  if (!Object.keys(byStore).length) {
    container.innerHTML = '<div class="card" style="text-align:center;padding:40px;color:var(--sub)">No staff match your filters.</div>';
  }
}

export function renderStaff() {
  const search = document.getElementById('staffSearch')?.value.toLowerCase() || '';
  const areaF = document.getElementById('filterArea')?.value || '';
  const storeF = document.getElementById('filterStore')?.value || '';
  const roleF = document.getElementById('filterRole')?.value || '';
  const view = document.getElementById('filterView')?.value || 'grouped';

  // v129.11 (the Qatar AM four, 2 Aug) — the AM's boundary is their STORE list
  // (getVisibleStaff already applies it). The old forced `s.area === area`
  // second gate silently hid any staff whose row lacked area_id — exactly
  // what the Amanah server apply produced (it never wrote area_id), and what
  // a stale cached snapshot serves even after the data is fixed. HO keeps
  // the OPTIONAL area dropdown; AMs never re-filter on the fragile per-row
  // area field again.
  const effectiveArea = currentAdmin?.role === 'area_manager' ? '' : areaF;

  let filtered = getVisibleStaff();
  if (effectiveArea) filtered = filtered.filter(s => s.area === effectiveArea);
  if (storeF) filtered = filtered.filter(s => s.store === storeF);
  if (roleF) filtered = filtered.filter(s => s.role === roleF);
  if (search) filtered = filtered.filter(s => s.name.toLowerCase().includes(search));

  const staffCountEl = document.getElementById('staffCount');
  if (staffCountEl) staffCountEl.textContent = '(' + filtered.length + ' staff)';
  const reqBadgeEl = document.getElementById('reqBadge');
  if (reqBadgeEl) reqBadgeEl.textContent = requestsData.filter(r => r.status === 'pending').length;

  const container = document.getElementById('staffContent');

  if (view === 'table') {
    renderStaffTable(filtered, container);
  } else {
    renderStaffGrouped(filtered, container);
  }
}

export async function archiveStaff() {
  const id = editingStaffId;
  const s = staffData.find(x => x.id === id);
  if (!s) return;
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Archive ' + s.name + '? They will be removed from rotas. This can be undone by an admin.', { danger: true, confirmLabel: 'Archive' }))) return;
  s.active = false;
  changeLog.unshift({id:Date.now(),staffId:id,type:'archive',from:{},to:{},effectiveFrom:todayDateStr(),scheduledBy:currentAdmin.name,appliedAt:new Date().toISOString(),createdAt:new Date().toISOString()});
  persistStaffData(id);
  closeStaffModal(); showToast(s.name + ' archived','success'); renderStaff(); renderDashboard();
}

// Overnight fix (sensitive): dated (future) staff changes and future start
// dates are refused in both modals until real scheduling exists. Both values
// are YYYY-MM-DD strings from <input type="date">, compared as strings (the
// same way this file always has). Not exported: the Uploads bundle's export
// list is the onclick surface.
const DATED_CHANGES_UNSUPPORTED = 'Dated changes are not supported yet. Make the change on the day, or save it now with today\'s date.';
function isFutureEffectiveDate(dateStr, today) {
  return !!(dateStr && today && String(dateStr) > String(today));
}

// v136.6: the same easy-to-guess list Add Staff refuses (addStaff's TRIVIAL_PINS, v76), now refused on Edit too.
const EDIT_TRIVIAL_PINS = new Set([
  '000000', '111111', '222222', '333333', '444444',
  '555555', '666666', '777777', '888888', '999999',
  '123456', '654321', '012345',
]);

export async function saveStaff() {
  const id = editingStaffId;
  const s = staffData.find(x => x.id === id);
  if (!s) return;

  const newName = document.getElementById('sfName').value.trim();
  // v59: emp_code is required + unique
  const newEmpCode = (document.getElementById('sfEmpCode').value || '').trim().toUpperCase();
  if (!newEmpCode) {
    showToast('Employee code is required', 'error');
    document.getElementById('sfEmpCode').focus();
    return;
  }
  // Client-side dup check — server still enforces via UNIQUE constraint, but
  // catching it here gives a clearer error than a 409.
  const dupStaff = staffData.find(x => x.id !== id && (x.empCode || '').toUpperCase() === newEmpCode);
  if (dupStaff) {
    showToast('Employee code "' + newEmpCode + '" is already used by ' + dupStaff.name, 'error');
    document.getElementById('sfEmpCode').focus();
    return;
  }
  const newRole = document.getElementById('sfRole').value;
  const newStore = document.getElementById('sfStore').value;
  const newArea = document.getElementById('sfArea').value;
  const newCountry = document.getElementById('sfCountry').value;
  const newDayOff = document.getElementById('sfDayOff').value;
  const newContractedHours = parseInt(document.getElementById('sfContractedHours').value) || 48;
  // v60 hotfix: PIN is null when blank (means "keep existing"). Only update
  // when the admin actually typed something. Previous code defaulted to
  // '000000' which silently wiped PINs on every save without typed input.
  const rawPin = document.getElementById('sfPin').value.trim();
  let newPin = null;
  if (rawPin) {
    // v136.6 (Matt, 16 Oct 2026: "make the pin box strict"): exactly 6 digits, the same rule as Add Staff. It used to
    // accept 1 to 6 digits and quietly pad with zeros, so a typed 4728 was saved as 004728 and the person's own
    // 4728 then came back "Invalid PIN" (the new-starter report that day).
    if (!/^\d{6}$/.test(rawPin)) {
      showToast('PIN must be exactly 6 digits', 'error');
      document.getElementById('sfPin').focus();
      return;
    }
    if (EDIT_TRIVIAL_PINS.has(rawPin)) {
      showToast('That PIN is too easy to guess — please pick something less obvious', 'error');
      document.getElementById('sfPin').focus();
      return;
    }
    newPin = rawPin;
  }

  // v61: if admin typed a new PIN, check for duplicates in the same store.
  // Same-store duplicates cause clock-in ambiguity (we hit this with Alpha
  // and Charlie both being 000000). Cross-store collisions are harmless
  // because clock lookup is store-scoped. Excludes this staff so admins
  // can re-save without being warned about their own PIN.
  if (newPin && newStore) {
    try {
      const dupRes = await DB.staff.checkPinDuplicate(newStore, newPin, editingStaffId);
      const dups = (dupRes && dupRes.duplicates) || [];
      if (dups.length > 0) {
        const names = dups.map(d => d.name).join(', ');
        const proceed = confirm(
          'PIN ' + newPin + ' is already used by ' + names + ' in this store.\n\n' +
          'If two staff share a PIN, neither will be able to clock in (the system can\'t tell who\'s who).\n\n' +
          'Save anyway?'
        );
        if (!proceed) return;
      }
    } catch(e) {
      console.warn('[saveStaff] PIN dup check failed:', e.message);
      // Don't block save on a check failure — log and continue
    }
  }
  const effectiveFrom = document.getElementById('sfEffectiveFrom').value;
  const today = todayDateStr();

  // v60 hotfix: refuse to save if the store dropdown is empty/unset. Without
  // this guard, sfStore.value could be '' (because the dropdown didn't contain
  // the real store) and a transfer would be recorded that wipes the staff's
  // real store assignment. Real data-loss bug.
  if (!newStore) {
    showToast('Cannot save — store is not selected. Refresh the page and try again.', 'error');
    return;
  }

  const changes = [];
  if (newName !== s.name) changes.push({type:'name_change', from:{name:s.name}, to:{name:newName}});
  if (newRole !== s.role) changes.push({type:'role_change', from:{role:s.role}, to:{role:newRole}});
  if (newStore !== s.store) changes.push({type:'transfer', from:{store:s.store,area:s.area,country:s.country}, to:{store:newStore,area:newArea,country:newCountry}});

  // Overnight fix (sensitive): a future "Date effective from" was only ever
  // kept in the in-memory changeLog. Nothing applies it on the day, and the
  // PIN, day off and hours typed alongside it were dropped too, while the
  // toast said "scheduled". Until real scheduling exists, refuse the date
  // (before anything is changed) so nothing is silently lost.
  if (changes.length && isFutureEffectiveDate(effectiveFrom, today)) {
    showToast(DATED_CHANGES_UNSUPPORTED, 'error');
    return;
  }

  // v59: empCode change is a quiet update — not in the changeLog (not a transfer/role/name)
  // but still needs persisting to staffData and DB.
  s.empCode = newEmpCode;

  if (!changes.length && !newDayOff !== !s.dayOff) {
    // Just a day off update — apply immediately
    s.dayOff = newDayOff || null;
    s.contractedHours = newContractedHours;
    if (newPin !== null) s.pin = newPin;  // v60: PIN may have been typed alongside day-off
    persistStaffData(editingStaffId);
    closeStaffModal(); showToast('Day off updated','success'); renderStaff(); return;
  }
  if (!changes.length) {
    // Could still be a contracted hours change, PIN change, or empCode change
    s.contractedHours = newContractedHours;
    s.dayOff = newDayOff || null;
    if (newPin !== null) s.pin = newPin;  // v60: only update if admin typed a new PIN
    persistStaffData(editingStaffId);
    closeStaffModal(); showToast('Changes saved ✓','success'); renderStaff(); return;
  }

  const isFuture = effectiveFrom && effectiveFrom > today;

  if (isFuture) {
    // Unreachable since the overnight fix above (future dates are refused);
    // kept so real scheduling can pick it up. Schedule the changes
    changes.forEach(c => {
      changeLog.unshift({
        id: Date.now() + Math.random(),
        staffId: id,
        ...c,
        effectiveFrom,
        scheduledBy: currentAdmin.name,
        appliedAt: null,
        createdAt: new Date().toISOString()
      });
    });
    closeStaffModal();
    showToast('Change scheduled for ' + effectiveFrom + ' ✓', 'success');
  } else {
    // Apply immediately
    changes.forEach(c => {
      if (c.type === 'name_change') s.name = c.to.name;
      if (c.type === 'role_change') s.role = c.to.role;
      if (c.type === 'transfer') { s.store = c.to.store; s.area = c.to.area; s.country = c.to.country; }
      changeLog.unshift({
        id: Date.now() + Math.random(),
        staffId: id,
        ...c,
        effectiveFrom: today,
        scheduledBy: currentAdmin.name,
        appliedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      });
    });
    s.dayOff = newDayOff || null;
    s.contractedHours = newContractedHours;
    if (newPin !== null) s.pin = newPin;  // v60: PIN change can ride along with name/role/store edits
    closeStaffModal();
    showToast('Changes saved ✓', 'success');
  }
  // BUG FIX (May 1, 2026): use the captured `id` from line 3380, not the
  // global `editingStaffId`. closeStaffModal() at line 3519 resets the
  // global to null. With the global, persistStaffData receives null, the
  // `if (changedId)` guard inside skips the DB write, and the change is
  // silently lost. Result: every immediate transfer/role-change/name-change
  // since this branch was added has been silently failing — toast says
  // "Changes saved ✓", local state and changeLog update, but the staff
  // row in the DB never changes. Spotted when Echo's transfer reverted
  // on page refresh.
  persistStaffData(id);
  renderStaff(); renderDashboard(); renderChangelog(); updateChangesBadge();
}


// ════════════════════════════════════════════════════════════
// v104.0 additions — Staff (v104 extension)
// ════════════════════════════════════════════════════════════
// Final 3 staff CRUD remnants joining the existing 25.
// Uploads.staff is now the COMPLETE Staff surface.

export function openStaffModal(id) {
  editingStaffId = id;
  const s = staffData.find(x => x.id === id);
  if (!s) return;

  document.getElementById('staffModalTitle').textContent = 'Edit Staff Member';
  const subEl = document.getElementById('staffModalSub');
  subEl.textContent = s.name + ' · ' + s.store;
  subEl.setAttribute('translate', 'no');   // v135.2: a name and a store, shown as written
  document.getElementById('sfName').value = s.name;
  // v59: emp_code is now required + unique. Field always populated; validation on save.
  document.getElementById('sfEmpCode').value = s.empCode || '';
  document.getElementById('sfRole').value = s.role;
  document.getElementById('sfCountry').value = s.country || 'ELC - UAE';
  document.getElementById('sfArea').value = s.area || getDefaultAreaId();
  updateStoreDropdown();
  // v60 hotfix: if staff's current store isn't in the dropdown options,
  // inject it. Otherwise `sel.value = s.store` silently fails, sfStore
  // reverts to the first option, and saveStaff records a false "transfer"
  // that overwrites their real store with junk. Real data-loss bug.
  (function ensureStoreOption() {
    const sel = document.getElementById('sfStore');
    if (!sel || !s.store) return;
    const exists = Array.from(sel.options).some(o => o.value === s.store);
    if (!exists) {
      const o = document.createElement('option');
      o.value = s.store;
      o.textContent = s.store + ' (current — not in current store list)';
      sel.insertBefore(o, sel.firstChild);
    }
  })();
  document.getElementById('sfStore').value = s.store;
  document.getElementById('sfDayOff').value = s.dayOff || '';
  // Contracted hours — auto-detect Emirati if not set
  const EMIRATI_SUFFIXES_ADMIN = ['alshehhi','almeqbaali','almheiri','almazrouei','alnuaimi','aldhaheri','alkaabi','alketbi','alremeithi','alsuwaidi','alhameli','alblooshi','alzaabi','alrashidi','almansouri','alaali','albloushi','alshamsi','alobaidli','almuhairi'];
  const isEmiratiAdmin = name => EMIRATI_SUFFIXES_ADMIN.some(s => name.toLowerCase().replace(/\s+/g,'').includes(s));
  const contracted = s.contractedHours != null ? s.contractedHours : (isEmiratiAdmin(s.name) ? 36 : 48);
  const autoDetected = s.contractedHours == null && isEmiratiAdmin(s.name);
  document.getElementById('sfContractedHours').value = String(contracted);
  document.getElementById('sfContractedNote').style.display = autoDetected ? '' : 'none';
  // v60 hotfix: never populate the PIN field. The /api/staff response no
  // longer includes `pin` (only `pin_length` for digit-count display), so
  // s.pin was always undefined and falling through to '000000' — meaning
  // every Save without typing a new PIN was resetting the staff's PIN to
  // 000000. Critical data-loss bug.
  document.getElementById('sfPin').value = '';
  // v60: reset the reveal UI so a stale revealed PIN doesn't appear when
  // opening a different staff record.
  hideRevealedPin();
  // v61: render consent status + reset button for THIS staff
  Uploads.staff.renderStaffConsentStatus(s);
  // v133.116: "Also manages" (src/staffAdmin); a store manager only
  if (typeof window.renderAlsoManages === 'function') window.renderAlsoManages(s);
  document.getElementById('sfEffectiveFrom').value = '';
  document.getElementById('sfArchiveBtn').style.display = currentAdmin.role === 'admin' ? '' : 'none';


  // v133.52: promotions and acting roles (src/staffRoles)
  if (typeof StaffRoles !== 'undefined' && StaffRoles.load) StaffRoles.load(id);
  document.getElementById('staffModal').classList.add('open');
}

export function closeStaffModal() { document.getElementById('staffModal').classList.remove('open'); editingStaffId = null; hideRevealedPin(); }

export async function saveNewStaff() {
  const name = document.getElementById('newSfName').value.trim();
  if (!name) { showToast('Enter a name','error'); return; }
  // v59: emp_code required + unique
  const empCode = (document.getElementById('newSfEmpCode').value || '').trim().toUpperCase();
  if (!empCode) {
    showToast('Employee code is required', 'error');
    document.getElementById('newSfEmpCode').focus();
    return;
  }
  const dupStaff = staffData.find(x => (x.empCode || '').toUpperCase() === empCode);
  if (dupStaff) {
    showToast('Employee code "' + empCode + '" is already used by ' + dupStaff.name, 'error');
    document.getElementById('newSfEmpCode').focus();
    return;
  }

  // v76: validate PIN — must be 6 digits, must not be a trivial sequence
  const pinInput = document.getElementById('newSfPin');
  const newPin = (pinInput?.value || '').trim();
  if (!/^\d{6}$/.test(newPin)) {
    showToast('PIN must be exactly 6 digits', 'error');
    pinInput?.focus();
    return;
  }
  // Reject the worst defaults — these are guaranteed to leak across stores
  // and pre-v76 production has many staff with these PINs already.
  const TRIVIAL_PINS = new Set([
    '000000', '111111', '222222', '333333', '444444',
    '555555', '666666', '777777', '888888', '999999',
    '123456', '654321', '012345',
  ]);
  if (TRIVIAL_PINS.has(newPin)) {
    showToast('That PIN is too easy to guess — please pick something less obvious', 'error');
    pinInput?.focus();
    return;
  }

  const role = document.getElementById('newSfRole').value;
  const country = document.getElementById('newSfCountry').value;
  const area = document.getElementById('newSfArea').value;
  const store = document.getElementById('newSfStore').value;
  const startDate = document.getElementById('newSfStartDate').value;

  // Overnight fix (sensitive): a future start date created the person as
  // INACTIVE and nothing ever switched them on, so they never appeared on a
  // rota or the clock, while the toast said "scheduled to start". Refuse it
  // until real scheduling exists; nothing has been written at this point.
  if (isFutureEffectiveDate(startDate, todayDateStr())) {
    showToast(DATED_CHANGES_UNSUPPORTED, 'error');
    document.getElementById('newSfStartDate').focus();
    return;
  }

  // v76: check PIN isn't already used by another staff member in the SAME store.
  // Cross-store duplicates are fine (PIN scope is per-store).
  if (DB && DB.staff && DB.staff.checkPinDuplicate) {
    try {
      const dupCheck = await DB.staff.checkPinDuplicate(store, newPin, null);
      if (dupCheck && dupCheck.duplicates && dupCheck.duplicates.length > 0) {
        const names = dupCheck.duplicates.map(d => d.name).join(', ');
        const proceed = confirm(
          'PIN ' + newPin + ' is already used by ' + names + ' at ' + store + '.\n\n' +
          'PINs need to be unique within a store so the clock-in flow can identify the right person.\n\n' +
          'Pick a different PIN — tap OK to go back and change it.'
        );
        // Either choice means "don't save" — the duplicate must be resolved first
        pinInput?.focus();
        return;
      }
    } catch(e) {
      console.warn('[saveNewStaff] PIN dup check failed (proceeding):', e.message);
      // If the check itself fails (network, etc.), fall through and create anyway.
      // Worst case: the DB upsert later catches it. Better than blocking on a
      // network blip.
    }
  }

  const today = todayDateStr();
  const newId = Math.max(...staffData.map(s=>s.id), 0) + 1;
  const isActive = !(startDate && startDate > today);
  const newStaff = {id:newId,country,area,store,name,role,empCode,color:COLORS[newId%COLORS.length],dayOff:null,toilBalance:0,weekHours:0,active:isActive,pin:newPin};

  staffData.push(newStaff);
  changeLog.unshift({id:Date.now(),staffId:newId,type:'add',from:{},to:{store,area,country,name,role},effectiveFrom:isActive?today:startDate,scheduledBy:currentAdmin.name,appliedAt:isActive?new Date().toISOString():null,createdAt:new Date().toISOString()});
  showToast(isActive ? name + ' added ✓' : name + ' scheduled to start ' + startDate, 'success');

  // Write to DB. PIN now comes from the form field (not hardcoded).
  DB.staff.create({
    name, role, store: store, area_id: area, country, emp_code: empCode,
    color: COLORS[newId%COLORS.length], active: isActive, pin: newPin,
  }).then(result => {
    // Update local ID to match DB-assigned ID
    if (result && result.id) newStaff.id = result.id;
  }).catch(e => {
    console.error('[DB] Staff create failed:', e.message);
    showToast('⚠️ Staff create failed in DB — they may not appear on rotas', 'error');
  });

  persistStaffData();
  closeAddStaffModal(); Uploads.staff.renderStaff(); renderDashboard(); renderChangelog(); populateReqStaffSelect();
}

